import imageCompression from 'browser-image-compression';

// Enable/disable logging (set to false in production)
const DEBUG = true;

// Color codes for console (for better readability)
const LOG_STYLES = {
    info: 'color: #2196f3; font-weight: bold',
    success: 'color: #4caf50; font-weight: bold',
    warning: 'color: #ff9800; font-weight: bold',
    error: 'color: #f44336; font-weight: bold',
    compression: 'color: #9c27b0; font-weight: bold',
    size: 'color: #00bcd4; font-weight: bold'
};

// Helper function for formatted logging
const log = (message, type = 'info', data = null) => {
    if (!DEBUG) return;

  
};

// Add this helper function if you don't have it
export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// Get detailed file information
export const getFileInfo = (file) => {
    return {
        name: file.name,
        type: file.type,
        size: file.size,
        sizeFormatted: formatBytes(file.size),
        lastModified: new Date(file.lastModified).toLocaleString(),
        dimensions: null // Will be filled for images
    };
};

// Get image dimensions (useful for monitoring)
export const getImageDimensions = (file) => {
    return new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        img.src = url;
    });
};

/**
 * Smart compression for images with full monitoring
 * Videos pass through (handled by backend Spring Boot)
 */
export const compressMedia = async (file, options = {}) => {
    const startTime = Date.now();
    const fileType = file.type;
    const fileSizeMB = file.size / (1024 * 1024);

    // log('═══════════════════════════════════════', 'info');
    // log('📁 COMPRESSION STARTED', 'compression');
    // log('═══════════════════════════════════════', 'info');

    // // Log original file info
    // log('📄 Original File Details:', 'info');
   

    // For images - compress on frontend
    if (fileType.startsWith('image/')) {
        // Get image dimensions if possible
        try {
            const dimensions = await getImageDimensions(file);
            if (dimensions) {
                // log('📐 Image Dimensions:', 'size');
                // console.table(dimensions);
            }
        } catch (err) {
            log('Could not get dimensions', 'warning');
        }

        const compressed = await compressImage(file, fileSizeMB, options);
        const endTime = Date.now();
        const timeTaken = ((endTime - startTime) / 1000).toFixed(2);

        // log('═══════════════════════════════════════', 'success');
        // log('✅ COMPRESSION COMPLETE', 'success');
        // log(`⏱️  Time taken: ${timeTaken} seconds`, 'info');
        // log('═══════════════════════════════════════', 'success');

        return compressed;
    }

    // For videos - skip frontend compression (backend Spring Boot handles)
    if (fileType.startsWith('video/')) {
        // log('🎥 VIDEO FILE DETECTED', 'warning');
        // log('⏩ Skipping frontend compression (will be compressed by Spring Boot backend)', 'warning');
        // log('📊 Video Info:', 'info');
        // console.table({
        //     'File Name': file.name,
        //     'File Type': fileType,
        //     'File Size': formatBytes(file.size),
        //     'Size in MB': `${fileSizeMB.toFixed(2)} MB`
        // });

        const endTime = Date.now();
        const timeTaken = ((endTime - startTime) / 1000).toFixed(2);
        // log(`⏱️  Processing time: ${timeTaken} seconds (no compression applied)`, 'info');
        // log('═══════════════════════════════════════', 'info');

        return file;
    }

    // // For other files
    // log('📄 OTHER FILE TYPE DETECTED', 'warning');
    // log('⏩ No compression applied', 'warning');
    // log('═══════════════════════════════════════', 'info');

    return file;
};

// Smart image compression based on file size
const compressImage = async (file, fileSizeMB) => {
    // Determine compression based on file size
    let options;

    if (fileSizeMB <= 2) {
        // Files under 2MB - no compression needed
        return file;
    }
    else if (fileSizeMB <= 5) {
        // 2-5MB files - light compression for quality preservation
        options = {
            maxSizeMB: 1.8,        // Target ~1.8MB (NOT 800KB)
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality: 0.92    // 92% quality - excellent
        };
    }
    else if (fileSizeMB <= 10) {
        // 5-10MB files - moderate compression
        options = {
            maxSizeMB: 2.5,        // Target ~2.5MB
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality: 0.88    // 88% quality - very good
        };
    }
    else {
        // 10MB+ files - stronger but still quality-focused
        options = {
            maxSizeMB: 3.0,        // Target ~3MB (NOT 143KB!)
            maxWidthOrHeight: 3000,
            useWebWorker: true,
            fileType: 'image/jpeg',
            initialQuality: 0.88    // 85% quality - good
        };
    }


    try {
        const compressed = await imageCompression(file, options);
        const savedPercent = ((1 - compressed.size / file.size) * 100).toFixed(1);
   
        return compressed;
    } catch (error) {
        console.error('Compression failed:', error);
        return file;
    }
};

// Monitor received data (for when you fetch from server)
export const monitorReceivedFile = async (url, fileName) => {
    // log('═══════════════════════════════════════', 'info');
    // log('📥 RECEIVING FILE FROM SERVER', 'info');
    // log(`🔗 URL: ${url}`, 'info');
    // log(`📄 File Name: ${fileName}`, 'info');

    try {
        const response = await fetch(url);
        const blob = await response.blob();

        // log('📊 RECEIVED FILE DETAILS:', 'size');
        // console.table({
        //     'File Name': fileName,
        //     'File Type': blob.type,
        //     'File Size': formatBytes(blob.size),
        //     'Size in MB': `${(blob.size / (1024 * 1024)).toFixed(2)} MB`,
        //     'Size in KB': `${(blob.size / 1024).toFixed(2)} KB`
        // });

        return blob;
    } catch (error) {
        // log('❌ Failed to fetch file details', 'error');
        console.error(error);
        return null;
    }
};



// Add this function to your existing mediaCompression.js file

/**
 * Monitor received image from server - shows size and quality info
 * @param {string} imageUrl - URL of the image to monitor
 * @param {string} imageName - Name of the image for logging
 * @returns {Promise<object>} - Returns image details
 */
export const monitorReceivedImage = async (imageUrl, imageName) => {

    try {
        const startTime = Date.now();
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const endTime = Date.now();

        // Check if it's an image
        if (blob.type.startsWith('image/')) {
            // Get image dimensions after decompression
            const img = new Image();
            const imageUrl_local = URL.createObjectURL(blob);

            await new Promise((resolve) => {
                img.onload = () => {
                 

                    URL.revokeObjectURL(imageUrl_local);
                    resolve();
                };
                img.onerror = () => {
                    URL.revokeObjectURL(imageUrl_local);
                    resolve();
                };
                img.src = imageUrl_local;
            });
        }


        return {
            size: blob.size,
            type: blob.type,
            sizeFormatted: formatBytes(blob.size)
        };

    } catch (error) {
        console.error('%c❌ Failed to fetch image details:', 'color: #f44336; font-weight: bold', error);
        return null;
    }
};